import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  TrendingDown, 
  Clock, 
  DollarSign, 
  BarChart3,
  ArrowRight,
  Check,
} from 'lucide-react';

// Platform Economics - Step 9
const PlatformEconomics = () => {
  return (
    <div className="min-h-screen bg-[#F6F7FB]">
      <Helmet>
        <title>Platform Economics | Liquicent</title>
        <meta name="description" content="Learn how Liquicent reduces coordination costs and improves enterprise efficiency through AI-powered workflows." />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-20 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-[#0F172A] mb-6">
              Platform Economics
            </h1>
            <p className="text-xl text-[#475569] leading-relaxed">
              How Liquicent reduces coordination costs and transforms enterprise operations
            </p>
          </div>
        </div>
      </section>

      {/* Coordination Problem */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-sm font-semibold text-[#1A3A8F] uppercase tracking-wider mb-4">
                The Coordination Problem
              </h2>
              <h3 className="text-3xl font-bold text-[#0F172A] mb-6">
                Enterprises Spend 40-60% of Time on Coordination
              </h3>
              <p className="text-lg text-[#475569] mb-6 leading-relaxed">
                Every decision in an organization requires information gathering, stakeholder alignment, 
                approval workflows, and documentation. This coordination overhead scales quadratically 
                with organization size.
              </p>
              <p className="text-lg text-[#475569] leading-relaxed">
                Traditional automation tools address individual tasks but don't solve the fundamental 
                coordination problem—they often create more coordination work by fragmenting processes 
                across disconnected systems.
              </p>
            </div>

            <Card className="p-8 bg-[#F6F7FB] border border-[#E5E7EB] rounded-2xl">
              <h4 className="font-semibold text-[#0F172A] mb-6">Coordination Cost Breakdown</h4>
              <div className="space-y-4">
                <CostBar label="Information Gathering" percentage={25} />
                <CostBar label="Stakeholder Alignment" percentage={20} />
                <CostBar label="Approval Workflows" percentage={18} />
                <CostBar label="Documentation & Audit" percentage={15} />
                <CostBar label="Follow-up & Escalation" percentage={12} />
              </div>
              <div className="mt-6 pt-6 border-t border-[#E5E7EB]">
                <p className="text-sm text-[#64748B]">
                  Source: Internal enterprise workflow analysis, n=50 companies
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Transaction Cost Model */}
      <section className="py-20 bg-[#F6F7FB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-sm font-semibold text-[#2CB1A1] uppercase tracking-wider mb-4">
              Transaction Cost Model
            </h2>
            <h3 className="text-3xl font-bold text-[#0F172A] mb-4">
              The Economics of AI-Driven Workflows
            </h3>
            <p className="text-lg text-[#475569] max-w-2xl mx-auto">
              Liquicent applies transaction cost economics to enterprise workflows
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 bg-white border border-[#E5E7EB] rounded-2xl">
              <div className="w-14 h-14 rounded-xl bg-[#1A3A8F]/10 flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-[#1A3A8F]" />
              </div>
              <h4 className="text-xl font-semibold text-[#0F172A] mb-3">Search Costs</h4>
              <p className="text-[#475569] mb-4">
                Cost of finding relevant information, identifying stakeholders, and locating appropriate 
                approvers for each decision.
              </p>
              <div className="text-3xl font-bold text-[#2CB1A1]">-70%</div>
              <p className="text-sm text-[#64748B]">With Liquicent agents</p>
            </Card>

            <Card className="p-8 bg-white border border-[#E5E7EB] rounded-2xl">
              <div className="w-14 h-14 rounded-xl bg-[#2CB1A1]/10 flex items-center justify-center mb-6">
                <DollarSign className="w-7 h-7 text-[#2CB1A1]" />
              </div>
              <h4 className="text-xl font-semibold text-[#0F172A] mb-3">Bargaining Costs</h4>
              <p className="text-[#475569] mb-4">
                Cost of negotiating terms, aligning stakeholders, and reaching consensus on decisions 
                across departments.
              </p>
              <div className="text-3xl font-bold text-[#2CB1A1]">-60%</div>
              <p className="text-sm text-[#64748B]">With automated workflows</p>
            </Card>

            <Card className="p-8 bg-white border border-[#E5E7EB] rounded-2xl">
              <div className="w-14 h-14 rounded-xl bg-[#1A3A8F]/10 flex items-center justify-center mb-6">
                <BarChart3 className="w-7 h-7 text-[#1A3A8F]" />
              </div>
              <h4 className="text-xl font-semibold text-[#0F172A] mb-3">Enforcement Costs</h4>
              <p className="text-[#475569] mb-4">
                Cost of monitoring compliance, documenting decisions, and maintaining audit trails 
                for governance.
              </p>
              <div className="text-3xl font-bold text-[#2CB1A1]">-85%</div>
              <p className="text-sm text-[#64748B]">With Unified Transaction Graph</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Coordination Cost Formula */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-sm font-semibold text-[#1A3A8F] uppercase tracking-wider mb-4">
                Coordination Cost Formula
              </h2>
              <h3 className="text-3xl font-bold text-[#0F172A] mb-6">
                Quantifying the Impact
              </h3>
              <p className="text-lg text-[#475569] mb-6 leading-relaxed">
                We model coordination cost as a function of organizational complexity, decision frequency, 
                and stakeholder involvement.
              </p>
              
              <Card className="p-6 bg-[#F6F7FB] border border-[#E5E7EB] rounded-xl mb-6">
                <p className="font-mono text-lg text-[#0F172A] text-center">
                  C = α · n² · d · s / e
                </p>
              </Card>

              <div className="space-y-3 text-sm text-[#475569]">
                <p><strong>C</strong> = Total coordination cost</p>
                <p><strong>n</strong> = Number of stakeholders involved</p>
                <p><strong>d</strong> = Decision frequency</p>
                <p><strong>s</strong> = Stakeholder heterogeneity</p>
                <p><strong>e</strong> = Process efficiency (Liquicent impact)</p>
                <p><strong>α</strong> = Organization-specific constant</p>
              </div>
            </div>

            <Card className="p-8 bg-[#0F172A] text-white rounded-2xl">
              <h4 className="font-semibold text-xl mb-6">Convergence Rate</h4>
              <p className="text-[#94A3B8] mb-8">
                As Liquicent agents learn your organization's patterns, coordination costs 
                converge toward near-zero over time.
              </p>
              
              {/* Simplified Graph */}
              <div className="h-48 flex items-end justify-around gap-2 mb-6">
                {[100, 70, 45, 28, 18, 12, 8, 6, 5, 4].map((h, i) => (
                  <div 
                    key={i}
                    className="w-full bg-gradient-to-t from-[#1A3A8F] to-[#2CB1A1] rounded-t transition-all"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
              <div className="flex justify-between text-xs text-[#64748B]">
                <span>Month 1</span>
                <span>Month 10</span>
              </div>
              
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-[#2CB1A1]" />
                  <span className="font-medium">Average 85% reduction by Month 6</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-[#F6F7FB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-4">
              Enterprise Benefits
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { metric: '60%', label: 'Reduction in approval cycle time' },
              { metric: '3x', label: 'Faster decision velocity' },
              { metric: '85%', label: 'Lower coordination overhead' },
              { metric: '100%', label: 'Audit trail compliance' },
            ].map((item, i) => (
              <Card key={i} className="p-6 bg-white border border-[#E5E7EB] rounded-xl text-center">
                <div className="text-4xl font-bold text-[#1A3A8F] mb-2">{item.metric}</div>
                <p className="text-sm text-[#475569]">{item.label}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#1A3A8F] to-[#142D70] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Calculate Your Savings
          </h2>
          <p className="text-lg text-white/80 mb-8">
            See how much coordination overhead you can eliminate with Liquicent
          </p>
          <Link to="/request-demo">
            <Button className="bg-white text-[#1A3A8F] hover:bg-[#F6F7FB] px-8 py-6 text-base">
              Request Demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

const CostBar = ({ label, percentage }) => (
  <div>
    <div className="flex justify-between text-sm mb-1">
      <span className="text-[#475569]">{label}</span>
      <span className="font-medium text-[#0F172A]">{percentage}%</span>
    </div>
    <div className="h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
      <div 
        className="h-full bg-gradient-to-r from-[#1A3A8F] to-[#2CB1A1] rounded-full transition-all"
        style={{ width: `${percentage * 1.5}%` }}
      />
    </div>
  </div>
);

export default PlatformEconomics;
